import format from 'date-fns/format';
import { QuickButton } from '../conversation/types';
import { CaseSummary } from './caseSummary';
import { DEFAULT_PERSONALITY, PERSONALITIES, Personality, pickOne } from '../presets/personalities';
import { MAX_CASE_PICKS } from '../config';

export { MAX_CASE_PICKS };

/** Dates before this are placeholders (e.g. .NET's default 0001-01-01), not real values. */
const MIN_REAL_YEAR = 1900;

export function formatDate(value?: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getFullYear() < MIN_REAL_YEAR) return null;
  return format(date, 'MMM d, yyyy');
}

export function caseLink(c: Pick<CaseSummary, 'id'>): string | null {
  return c.id ? `/cases/${c.id}` : null;
}

/** The case's Messages tab, where the design team reads and replies. `focus=1` puts the cursor in the box. */
export function caseChatLink(c: Pick<CaseSummary, 'id'>): string | null {
  return c.id ? `/cases/${c.id}/case-chat?focus=1` : null;
}

/** "a", "a and b", "a, b and c". */
export function joinNatural(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** Status labels (from formatCaseStatus / the raw status) in the words a dentist would use. */
const STATUS_WORDS: Record<string, string> = {
  'Orders Received': 'received and waiting to start',
  'Case In Progress': 'in progress',
  'Finalizing Design': 'in final design',
  'Case For Dentist Approval': 'waiting for your approval',
  'Pending Clarification': 'waiting on a reply from you',
  Rescan: 'waiting on a rescan from you',
  Redesign: 'being redesigned',
  Shipped: 'shipped',
  Completed: 'completed',
  Cancelled: 'cancelled',
};

/** Provider steps (ServiceProviderType descriptions) in plain words. */
const PROVIDER_WORDS: Record<string, string> = {
  design: 'in design',
  'evident design center': 'in design',
  fabricate: 'in fabrication',
  'surgical kit': 'in surgical kit preparation',
};

/** Stages where the next move is the dentist's, so a missing due date is not worth mentioning. */
const WAITING_ON_DENTIST = new Set(['waiting for your approval', 'waiting on a reply from you', 'waiting on a rescan from you']);

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const daysBetween = (from: Date, to: Date): number => Math.max(0, Math.floor((to.getTime() - from.getTime()) / MS_PER_DAY));

const plural = (n: number, unit: string): string => `${n} ${unit}${n === 1 ? '' : 's'}`;

/** "Jane Doe · Crown and Night Guard", or "Case 1234" when there is no patient name. */
const subject = (c: CaseSummary): string => {
  const who = c.patientName || `Case ${c.caseRefNo}`;
  const what = joinNatural(c.treatments);
  return what ? `${who} · ${what}` : who;
};

/**
 * Where the case sits right now, as a predicate: "in design", "waiting for your approval". Anything the dentist
 * must act on comes first, then the provider step, then the status.
 */
export const stage = (c: CaseSummary): string | null => {
  if (c.flags?.rescan && !c.flags?.rescanUploaded) return STATUS_WORDS.Rescan;
  if (c.flags?.withApproval || c.statusLabel === 'Case For Dentist Approval') return STATUS_WORDS['Case For Dentist Approval'];
  if (c.statusLabel === 'Pending Clarification' || c.statusLabel === 'Rescan') return STATUS_WORDS[c.statusLabel];
  if (c.flags?.redesign || c.statusLabel === 'Redesign') return STATUS_WORDS.Redesign;
  if (c.latestProvider) return PROVIDER_WORDS[c.latestProvider.trim().toLowerCase()] || `in ${c.latestProvider.trim().toLowerCase()}`;
  if (c.statusLabel) return STATUS_WORDS[c.statusLabel] || `in ${c.statusLabel.toLowerCase()}`;
  return null;
};

/** The date to hold the lab to, and whether someone set it or the Hub estimated it. */
export const dueDate = (c: CaseSummary): { date: Date; estimated: boolean } | null => {
  const computed = parseRealDate(c.expectedReturnDate);
  if (computed) return { date: computed, estimated: c.expectedReturnSource !== undefined && c.expectedReturnSource !== 'scheduled' };
  const stored = parseRealDate(c.returnDate);
  return stored ? { date: stored, estimated: false } : null;
};

const isDone = (c: CaseSummary): boolean => (c.statusLabel === 'Completed' && !c.flags?.rescan) || c.statusLabel === 'Cancelled';

/** True when the case has a due (or Hub-estimated) date that has already passed. */
export const isOverdue = (c: CaseSummary, now: Date = new Date()): boolean => {
  if (isDone(c)) return false;
  const due = dueDate(c);
  return Boolean(due && due.date.getTime() < now.getTime());
};

/**
 * Fact first, in at most three short sentences: the stage in plain words, how long it has been there, and
 * the due date. A case past its date says so instead, with what Evie did about it.
 *   "Jane Doe · Crown is in design. It's been there 3 days. Due Oct 12, 2026."
 *   "Jane Doe · Crown is in design and is 4 days past its due date. I've flagged it to the team."
 * `flagged` is true once the overdue case has been filed with support this session; otherwise the reply
 * asks for a tap. Parts with missing data are left out.
 */
export function formatCaseDetail(c: CaseSummary, now: Date = new Date(), flagged = false): string {
  const completed = c.statusLabel === 'Completed' && !c.flags?.rescan;
  const where = completed ? STATUS_WORDS.Completed : stage(c);
  const updated = parseRealDate(c.lastUpdated);
  const due = isDone(c) ? null : dueDate(c);
  const overdueDays = due && due.date.getTime() < now.getTime() ? daysBetween(due.date, now) : 0;
  const waitingOnDentist = where !== null && WAITING_ON_DENTIST.has(where);

  let text: string;
  if (due && overdueDays > 0) {
    const label = due.estimated ? 'expected return' : 'due date';
    const past = `${plural(overdueDays, 'day')} past its ${label}`;
    text = where ? `${subject(c)} is ${where} and is ${past}.` : `${subject(c)} is ${past}.`;
    text += flagged ? " I've flagged it to the team." : ' Tap **Have support contact me** to flag it to the team.';
  } else {
    text = where ? `${subject(c)} is ${where}.` : `${subject(c)}.`;
    if (updated) {
      const days = daysBetween(updated, now);
      if (days === 0) text += ' It moved today.';
      else text += waitingOnDentist ? ` It's been waiting ${plural(days, 'day')}.` : ` It's been there ${plural(days, 'day')}.`;
    }
    if (due) {
      text += due.estimated ? ` Expected back by ${format(due.date, 'MMM d, yyyy')}.` : ` Due ${format(due.date, 'MMM d, yyyy')}.`;
    } else if (where && !completed && !waitingOnDentist && c.statusLabel !== 'Cancelled') {
      text += ' No due date on file yet.';
    }
  }

  const link = caseLink(c);
  return link ? `${text}\n[Open case](${link})` : text;
}

const parseRealDate = (value?: string): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) || date.getFullYear() < MIN_REAL_YEAR ? null : date;
};

/**
 * Why a case may feel slow, from what the Hub knows: who it is waiting on and whether it is inside its
 * expected window. One fact sentence, then one call to action (the design team on the case page).
 */
export function formatDelayExplanation(c: CaseSummary, now: Date = new Date()): string {
  const lines: string[] = [];
  lines.push(`**Case ${c.caseRefNo}**${c.patientName ? ` · ${c.patientName}` : ''}`);

  const due = dueDate(c)?.date ?? null;
  const dueText = due ? format(due, 'MMM d, yyyy') : null;
  const withProvider = c.latestProvider ? ` with ${c.latestProvider}` : '';

  if (c.flags?.rescan && !c.flags?.rescanUploaded) {
    lines.push("It's waiting on you: the lab needs a new scan uploaded before it can carry on.");
  } else if (c.flags?.withApproval || c.statusLabel === 'Case For Dentist Approval') {
    lines.push("It isn't stuck: the design is waiting for your approval and moves on as soon as you approve it.");
  } else if (c.statusLabel === 'Completed') {
    lines.push('It is marked as completed.');
  } else if (c.flags?.redesign) {
    lines.push("It's being redesigned, so the original dates may shift.");
  } else if (due && due.getTime() >= now.getTime()) {
    lines.push(`It's within its expected window and should be back with you by ${dueText}.`);
  } else if (due) {
    lines.push(`It's past its expected return date of ${dueText}.`);
  } else {
    lines.push(`It's in progress${withProvider} and I can't see a specific hold-up from here.`);
  }

  lines.push('For a firmer date, message the design team from the case page.');
  return lines.join('\n');
}

/** Every heading and hint across all voices; handy for tests. */
export const CASE_LIST_HEADINGS = PERSONALITIES.flatMap((p) => p.caseListHeadings);
export const CASE_LIST_HINTS = PERSONALITIES.flatMap((p) => p.caseListHints);

/**
 * One line above the case buttons: the heading and the tap hint, worded by the session's personality.
 * The cases themselves are the buttons (see `caseQuickButtons`), so they are not repeated as text.
 */
export function formatCaseList(
  cases: CaseSummary[],
  firstName?: string,
  intro?: string,
  pick: () => number = Math.random,
  personality: Personality = DEFAULT_PERSONALITY,
): string {
  const heading = (intro || pickOne(personality.caseListHeadings, pick)(firstName || '', cases.length)).replace(/:$/, '.');
  return `${heading} ${pickOne(personality.caseListHints, pick)}`;
}

/** Line 1: "1234 · Crown"; line 2: "Jane Doe · Oct 12, 2026". Either part is dropped when unknown. */
export function caseQuickButtons(cases: CaseSummary[], max = MAX_CASE_PICKS): QuickButton[] {
  return cases.slice(0, max).map((c) => {
    const treatment = joinNatural(c.treatments);
    const sublabel = [c.patientName, formatDate(c.sentDate)].filter(Boolean).join(' · ');
    return {
      label: treatment ? `${c.caseRefNo} · ${treatment}` : c.caseRefNo,
      ...(sublabel ? { sublabel } : {}),
      value: `case:${c.caseRefNo}`,
    };
  });
}
